import React from 'react';
import { Button, Typography } from '@material-ui/core';
import BackIcon from '@material-ui/icons/KeyboardBackspace';
import cs from 'classnames';
import { useParams, useHistory } from 'react-router-dom';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../../common/launchDarkly';
import NavHeader from '../../common/navigation/NavHeader';
import { Recipients } from '../../common/recipients';
import { RecipientRole, type IRecipient, type IGroup } from '../../common/recipients/types';
import { checkIfRecipientIsSender } from '../../common/utils';
import { Spinner, FooterLinks, Footer } from '../../components/common';
import { HeaderContext } from '../../context/header-context';
import { getUsersGroups } from '../../lib/api/account/accountApi';
import { useUpdateRecipients } from '../../lib/api/envelope';
import { preferencesUrl } from '../../lib/environment-config';
import { useIsSmallScreenMediaQuery } from '../../lib/isSmallScreen';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import { UserOrigins } from '../../lib/types';
import { applyColorAndCounterToNewRecipient, sortRecipients } from '../../lib/utils';
import { successToast, errorToast } from '../../store/pageFrame/actions';
import * as actions from '../../store/senderBuild/actions';
import { useInitRecipientManagement } from './use-init-recipient-management';

interface RecipientManagementProps {
  modal?: boolean;
  flags?: LaunchDarklyFlags;
  disableRecipientList?: boolean;
}

function RecipientManagement(props: RecipientManagementProps) {
  const { modal, flags, disableRecipientList } = props;
  const { contacts, settings, colorCounter, settingsFetched } = useShallowSelector((state) => ({
    settings: state.pageFrame.settings,
    colorCounter: state.pageFrame.recipientColorCounter,
    contacts: state.senderBuild.recipientsModal.contacts,
    settingsFetched: state.pageFrame.settingsFetched,
  }));

  const envelopeId = useParams<{ id: string }>().id;
  const history = useHistory();
  const dispatch = useDispatch();
  const isSmallScreen = useIsSmallScreenMediaQuery();
  const updateRecipients = useUpdateRecipients();
  const [recipients, setRecipients] = React.useState<IRecipient[]>([]);
  const [incompletePlaceholders, setIncompletePlaceholders] = React.useState<IRecipient[]>();
  const [optionalRecipients, setOptionalRecipients] = React.useState<IRecipient[]>();

  const isLoading = useInitRecipientManagement({
    modal,
    envelopeId,
    setRecipients,
    setIncompletePlaceholders,
    setOptionalRecipients,
  });

  const hideNavItems = flags && flags['hide-build-ui-elements'];

  const showPreferencesMenuItem = flags && flags['add-automatic-time-stamp-signatures-to-user-preferences'];
  const isStartSafeAccount = settingsFetched && settings?.account?.userOrigin === UserOrigins.DOP;
  const showAutomatedRemindersFlag = Boolean(
    flags && flags['automated-reminders'] && settingsFetched && !isStartSafeAccount
  );

  async function handleSubmit() {
    if (!recipients.length) return;

    try {
      const updatedRecipients: IRecipient[] = [...recipients];
      optionalRecipients?.forEach((or) => {
        if (!updatedRecipients.find((ur) => ur.id === or.id) && !checkIfRecipientIsSender(or)) {
          updatedRecipients.push(or);
        }
      });
      incompletePlaceholders?.forEach((ip) => {
        if (!updatedRecipients.find((ur) => ip.id === ur.id)) {
          updatedRecipients.push(ip);
        }
      });
      await updateRecipients.mutateAsync({ recipients: updatedRecipients, envelopeId });
      history.push(`/envelopes/${envelopeId}`);
      dispatch(successToast('Recipient changes saved.'));
    } catch {
      dispatch(errorToast('Error saving recipients.'));
    }
  }
  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleCreatePrimeContact = (contact: { firstName: string; lastName: string; email: string }) => {
    dispatch(actions.createPrimeContact(contact));
  };

  const headerContext = React.useContext(HeaderContext);
  React.useEffect(() => {
    if (modal) return;

    headerContext.setContent(
      <NavHeader
        labelName="Add recipients"
        breadcrumb={{ name: 'Envelope Management', onClick: () => history.push('/envelopes') }}
        progressBarPercent={75}
        hideNavItems={hideNavItems}
        preferencesUrl={preferencesUrl}
        showPreferences={showPreferencesMenuItem}
        getUsersGroups={getUsersGroups}
        flags={flags}
      />
    );
    return () => headerContext.setContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [modal]);

  function onChange(newRecipients: IRecipient[], newGroups: IGroup[]) {
    applyColorAndCounterToNewRecipient(newRecipients, recipients, colorCounter);
    sortRecipients(newRecipients);
    setRecipients(newRecipients);

    if (modal) {
      let newRecipientsTemp = incompletePlaceholders ? newRecipients.concat(incompletePlaceholders) : newRecipients;
      // Remove duplicates from newRecipientsTemp
      newRecipientsTemp = newRecipientsTemp.filter((nr, i) => newRecipientsTemp.indexOf(nr) === i);
      dispatch(actions.setRecipientsAndSignerGroupsModal(newRecipientsTemp, newGroups));
    }
  }

  if (isLoading || updateRecipients.isLoading) return modal ? <Spinner /> : null;

  const footer = modal ? null : (
    <Footer>
      {hideNavItems ? null : (
        <>
          <Button
            className="mr-auto"
            color="primary"
            onClick={() => window.history.back()}
            startIcon={<BackIcon fontSize="small" />}
          >
            Back
          </Button>
          <Button color="primary" data-spec="exit" onClick={() => history.push('/envelopes')}>
            Exit
          </Button>
        </>
      )}
      <Button
        variant="contained"
        color="primary"
        data-spec="next"
        onClick={handleSubmit}
        className="px-10"
        disabled={!recipients.length || !recipients.some((r) => r.role === RecipientRole.Signer && r.isActive)}
      >
        Next
      </Button>
    </Footer>
  );

  const title = modal ? null : (
    <div className={isSmallScreen ? 'pt-0 p-8 text-center' : 'p-8 text-center'}>
      <Typography variant="h3">Recipients & signing order</Typography>
      <Typography component="span" variant="body2" style={{ marginTop: 8 }}>
        Add recipients by clicking on the “+ New Recipient” button in the left side panel
      </Typography>
    </div>
  );

  return (
    <div className={cs('flex flex-col items-center w-full', modal ? 'h-full' : 'md:px-4')}>
      <div className="w-full max-w-7xl h-full max-h-[639px]">
        {title}
        <div
          className={cs(
            'bg-white',
            modal ? 'h-full' : 'min-h-[55vh]',
            !modal && 'border border-grey-400',
            !disableRecipientList && 'grid grid-flow-col'
          )}
          style={{ gridTemplateColumns: disableRecipientList ? undefined : modal ? '380px 1fr' : '530px 1fr' }}
        >
          <Recipients
            envelopeId={envelopeId}
            recipients={recipients}
            optionalRecipients={optionalRecipients}
            incompletePlaceholders={incompletePlaceholders}
            onChange={onChange}
            createPrimeContact={handleCreatePrimeContact}
            colorCounter={colorCounter}
            contacts={contacts}
            modal={modal}
            disableRecipientList={!!disableRecipientList}
            showAutomatedRemindersFlag={showAutomatedRemindersFlag}
          />
        </div>
        {footer}
        {!modal && !disableRecipientList && <FooterLinks />}
      </div>
    </div>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(RecipientManagement);
